import React from 'react'
import { useSelector } from 'react-redux'
import { Upload } from 'antd'
import { Http } from '../../../helpers/axois'
import { getRandomId } from '../../../utils/random-id'
import { useToast } from '../../../helpers/commonFunction'
import { validateFile } from './helpers'
import { AppState } from '../../../stateManager/store'
import { IScreenshot } from './type'

// icons
import { ReactComponent as ScreenshotsPlaceholderIcon } from '../../../assets/svg/uploadScreenshotsPlaceholder.svg'
import { ReactComponent as CloseIcon } from '../../../assets/svg/solo/closeBtn.svg'

const { Dragger } = Upload

interface IUploadScreenshots {
  screenshots: IScreenshot[]
  removeScreenshot: (url: string) => void
  setScreenshots: React.Dispatch<React.SetStateAction<IScreenshot[]>>
}

export const UploadScreenshots: React.FC<IUploadScreenshots> = ({ screenshots, removeScreenshot, setScreenshots  }) => {
  const orgId = useSelector((state: AppState) => state.org.selectedOrg?.id)
  const { ErrorToast } = useToast()

  const uploadImage = async ({ file }: any) => {
    const name = getRandomId() + '_' + file.name

    // only allow 5 screenshots per app
    if(screenshots.length > 4) {
      return
    }

    setScreenshots((prevState) => [
      ...prevState,
      {
        loading: true,
        name,
        url: '',
      },
    ])

    const formData = new FormData()
    formData.append('file', file, name)

    let url = ''
    try {
      const errMsg = validateFile(file, {
        type: 'image',
        size: 2,
      })
      if(!errMsg) {
        url = await onUpload(formData)
        return
      }
      throw new Error(errMsg)
    } catch (err) {
      ErrorToast(err.message)
    } finally {
      if (!url) {
        setScreenshots((prevState) =>
          prevState.filter((el) => el.name !== name)
        )
        return
      }
      setScreenshots(prevState => {
        const screenshots = prevState.map(el => el.name === name ? {...el, loading: false, url} : el)
        return screenshots
      })
    }
  }

  const onUpload = async (formData: FormData) => {
    const endpoint = `/orgs/${orgId}/marketplaceApps/image/upload`
    try {
      const res: any = await Http.post(endpoint, formData).toPromise()
      return res.url
    } catch (err) {
      const msg = err && err.message ? err.message : 'Failed to upload image'
      throw new Error(msg)
    }
  }

  const uploaderProps = {
    name: 'file',
    customRequest: uploadImage,
    showUploadList: false,
    multiple:true,
  }

  return (
    <div className="marketplace-app-screenshots">
      <Header count={screenshots.filter((el) => el.url).length} />
      <Dragger {...uploaderProps} disabled={screenshots.length > 4}>
        {!screenshots.length ? (
          <Placeholder />
        ) : (
          <Preview
            screenshots={screenshots}
            removeScreenshot={removeScreenshot}
          />
        )}
      </Dragger>
    </div>
  )
}

const Header: React.FC<{ count: number }> = ({ count }) => {
  const handleChooseFile = () => {
    const fileNode: HTMLInputElement | null = document.querySelector(
      `.create-marketplace-app .ant-upload input[type="file"]`
    )
    if (fileNode && count <= 4) {
      fileNode?.click()
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <p style={{ color: '#10002E' }}>App Preview & Screenshots</p>
      <div style={{ color: '#0466C8' }}>
        <span>{count}/5</span>
        <span>
          {'  '} | {'  '}
        </span>
        <span style={{ cursor: count > 4 ? 'not-allowed' : 'pointer' }} onClick={handleChooseFile}>
          Choose File
        </span>
      </div>
     </div>
  )
}

const Preview: React.FC<{
  screenshots: IScreenshot[]
  removeScreenshot: (name: string) => void
}> = ({ screenshots, removeScreenshot }) => {
  return (
    <div style={{ display: 'flex', gap: 36, padding: '0 24px' }}>
      {screenshots.map((screenshot) => {
        if (screenshot.loading) {
          return <div className="skeleton" key={screenshot.name}/>
        }
        return (
          <div style={{ position: 'relative', width: 350, height: 190 }} key={screenshot.name}>
            <img
              onClick={(e) => e.stopPropagation()}
              src={screenshot.url}
              alt={screenshot.name}
              style={{
                borderRadius: 5,
                boxShadow: '0px 3px 16px #95abd34b',
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              }}
            />
            <CloseIcon
              style={{
                cursor: 'pointer',
                position: 'absolute',
                right: -8,
                top: -8,
              }}
              width={22}
              height={22}
              onClick={(e) => {
                e.stopPropagation()
                removeScreenshot(screenshot.url)
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

const Placeholder = () => {
  return (
    <div>
      <ScreenshotsPlaceholderIcon />
      <p style={{ color: '#95ABD3', fontSize: 22, paddingTop: 22 }}>
        Drag and drop images here
      </p>
    </div>
  )
}
